/**
 * Email Campaign API Service
 * Handles all API calls for Email Templates, Campaigns, and Domains
 */

import { apiClient } from './aiCrmApi';
import {
  EmailTemplate,
  EmailCampaign,
  EmailCampaignLog,
  EmailDomain,
  CreateEmailTemplateInput,
  UpdateEmailTemplateInput,
  CreateEmailCampaignInput,
  UpdateEmailCampaignInput,
  CreateEmailDomainInput,
  UpdateEmailDomainInput,
  EmailTemplateFilters,
  EmailCampaignFilters,
  RecipientFilter,
  RecipientPreview,
  AIGeneratedTemplate,
  PaginatedResponse,
  ApiResponse,
  CampaignStatistics
} from '../types';

// Campaign Hints response type
export interface CampaignHintsResponse {
  company: { name: string | null; industry: string | null; services: string | null };
  topCampaigns: Array<{ name: string; subject: string; openRate: number; clickRate: number }>;
  businessProfile: {
    currentStage: string;
    primaryGoal: string;
    targetAudience: string;
    activeProducts: string[];
    painPoints: string[];
    tonePreference: string;
    promptCount: number;
  };
  advice: {
    campaign_details: string[];
    subject_lines: string[];
    email_content: string[];
    lead_generation: string[];
  };
  leadGenerationInsight: string | null;
  campaignInsight: string | null;
}

// ===== EMAIL TEMPLATES =====

export const emailTemplateApi = {
  /**
   * Get all email templates
   */
  getTemplates: async (
    filters?: EmailTemplateFilters & { limit?: number; offset?: number }
  ): Promise<PaginatedResponse<EmailTemplate>> => {
    const response = await apiClient.get<PaginatedResponse<EmailTemplate>>(
      '/email-templates',
      { params: filters }
    );
    return response.data;
  },

  /**
   * Get email template by ID
   */
  getTemplateById: async (id: string): Promise<EmailTemplate> => {
    const response = await apiClient.get<ApiResponse<EmailTemplate>>(
      `/email-templates/${id}`
    );
    return response.data.data;
  },

  /**
   * Create a new email template
   */
  createTemplate: async (data: CreateEmailTemplateInput): Promise<EmailTemplate> => {
    const response = await apiClient.post<ApiResponse<EmailTemplate>>(
      '/email-templates',
      data
    );
    return response.data.data;
  },

  /**
   * Update an email template
   */
  updateTemplate: async (
    id: string,
    data: UpdateEmailTemplateInput
  ): Promise<EmailTemplate> => {
    const response = await apiClient.put<ApiResponse<EmailTemplate>>(
      `/email-templates/${id}`,
      data
    );
    return response.data.data;
  },

  /**
   * Delete an email template
   */
  deleteTemplate: async (id: string): Promise<void> => {
    await apiClient.delete(`/email-templates/${id}`);
  },

  /**
   * Generate AI template
   */
  generateAITemplate: async (
    prompt: string,
    options?: {
      category?: string;
      recipientName?: string;
      recipientCompany?: string;
      tone?: string;
    }
  ): Promise<AIGeneratedTemplate> => {
    const response = await apiClient.post<ApiResponse<AIGeneratedTemplate>>(
      '/email-templates/generate-ai',
      {
        prompt,
        category: options?.category,
        recipientName: options?.recipientName,
        recipientCompany: options?.recipientCompany,
        tone: options?.tone
      }
    );
    return response.data.data;
  }
};

// ===== EMAIL CAMPAIGNS =====

export const emailCampaignApi = {
  /**
   * Get all email campaigns
   */
  getCampaigns: async (
    filters?: EmailCampaignFilters & { limit?: number; offset?: number }
  ): Promise<PaginatedResponse<EmailCampaign>> => {
    const response = await apiClient.get<PaginatedResponse<EmailCampaign>>(
      '/campaigns',
      { params: filters }
    );
    return response.data;
  },

  /**
   * Get campaign by ID with statistics
   */
  getCampaignById: async (
    id: string
  ): Promise<EmailCampaign & { statistics: CampaignStatistics }> => {
    const response = await apiClient.get<
      ApiResponse<EmailCampaign & { statistics: CampaignStatistics }>
    >(`/campaigns/${id}`);
    return response.data.data;
  },

  /**
   * Create a new campaign
   */
  createCampaign: async (data: CreateEmailCampaignInput): Promise<EmailCampaign> => {
    const response = await apiClient.post<ApiResponse<EmailCampaign>>(
      '/campaigns',
      data
    );
    return response.data.data;
  },

  /**
   * Update a campaign (draft only)
   */
  updateCampaign: async (
    id: string,
    data: UpdateEmailCampaignInput
  ): Promise<EmailCampaign> => {
    const response = await apiClient.put<ApiResponse<EmailCampaign>>(
      `/campaigns/${id}`,
      data
    );
    return response.data.data;
  },

  /**
   * Delete a campaign (draft only)
   */
  deleteCampaign: async (id: string): Promise<void> => {
    await apiClient.delete(`/campaigns/${id}`);
  },

  /**
   * Send/schedule a campaign
   */
  sendCampaign: async (
    id: string,
    scheduledAt?: Date | string
  ): Promise<EmailCampaign> => {
    const response = await apiClient.post<ApiResponse<EmailCampaign>>(
      `/campaigns/${id}/send`,
      { scheduledAt }
    );
    return response.data.data;
  },

  /**
   * Cancel a scheduled/sending campaign
   */
  cancelCampaign: async (id: string): Promise<EmailCampaign> => {
    const response = await apiClient.put<ApiResponse<EmailCampaign>>(
      `/campaigns/${id}/cancel`
    );
    return response.data.data;
  },

  /**
   * Reset a sent/failed/cancelled campaign back to draft for resending
   */
  resetCampaign: async (id: string): Promise<EmailCampaign> => {
    const response = await apiClient.put<ApiResponse<EmailCampaign>>(
      `/campaigns/${id}/reset`
    );
    return response.data.data;
  },

  /**
   * Get campaign logs
   */
  getCampaignLogs: async (
    id: string,
    filters?: { status?: string; limit?: number; offset?: number }
  ): Promise<PaginatedResponse<EmailCampaignLog>> => {
    const response = await apiClient.get<PaginatedResponse<EmailCampaignLog>>(
      `/campaigns/${id}/logs`,
      { params: filters }
    );
    return response.data;
  },

  /**
   * Preview recipients based on filter
   */
  previewRecipients: async (
    recipientType: string,
    recipientFilter?: RecipientFilter
  ): Promise<RecipientPreview> => {
    const response = await apiClient.post<ApiResponse<RecipientPreview>>(
      '/campaigns/preview-recipients',
      { recipientType, recipientFilter }
    );
    return response.data.data;
  },

  getCampaignHints: async (): Promise<CampaignHintsResponse> => {
    const response = await apiClient.get<ApiResponse<CampaignHintsResponse>>(
      '/campaigns/hints'
    );
    return response.data.data;
  },
};

// ===== EMAIL DOMAINS =====

export const emailDomainApi = {
  /**
   * Get all email domains
   */
  getDomains: async (): Promise<EmailDomain[]> => {
    const response = await apiClient.get<ApiResponse<EmailDomain[]>>(
      '/email-domains'
    );
    return response.data.data;
  },

  /**
   * Create a new email domain
   */
  createDomain: async (data: CreateEmailDomainInput): Promise<EmailDomain> => {
    const response = await apiClient.post<ApiResponse<EmailDomain>>(
      '/email-domains',
      data
    );
    return response.data.data;
  },

  /**
   * Update an email domain
   */
  updateDomain: async (
    id: string,
    data: UpdateEmailDomainInput
  ): Promise<EmailDomain> => {
    const response = await apiClient.put<ApiResponse<EmailDomain>>(
      `/email-domains/${id}`,
      data
    );
    return response.data.data;
  },

  /**
   * Delete an email domain
   */
  deleteDomain: async (id: string): Promise<void> => {
    await apiClient.delete(`/email-domains/${id}`);
  },

  /**
   * Verify an email domain
   */
  verifyDomain: async (
    id: string
  ): Promise<{ verificationStatus: string; verifiedAt?: string }> => {
    const response = await apiClient.post<
      ApiResponse<{ verificationStatus: string; verifiedAt?: string }>
    >(`/email-domains/${id}/verify`);
    return response.data.data;
  },

  /**
   * Send a test email
   */
  sendTestEmail: async (id: string, toEmail: string): Promise<void> => {
    await apiClient.post(`/email-domains/${id}/test`, { toEmail });
  }
};

// ===== DIRECT EMAIL SENDING =====

export interface SendEmailInput {
  to: string;
  toName?: string;
  subject: string;
  htmlContent: string;
  textContent?: string;
  fromName?: string;
  fromEmail?: string;
  replyTo?: string;
  leadId?: string;
  clientId?: string;
}

export interface SendEmailResult {
  messageId?: string;
  sentAt: string;
}

export const emailSendApi = {
  /**
   * Send a single email directly via SMTP
   */
  sendEmail: async (data: SendEmailInput): Promise<SendEmailResult> => {
    const response = await apiClient.post<ApiResponse<SendEmailResult>>(
      '/send-email',
      data
    );
    return response.data.data;
  }
};

export default {
  templates: emailTemplateApi,
  campaigns: emailCampaignApi,
  domains: emailDomainApi,
  send: emailSendApi
};
